import { ProductService } from './service';
import { FlatProduct, ProductsQueryParams } from './types';

/**
 * Maximum number of distinct products to request per page.
 * Chosen to stay well under typical SQL Server result-set size limits while
 * keeping export requests to a single round-trip for the vast majority of
 * real-world datasets (total distinct products in the DB today: ~2 000).
 */
const EXPORT_PAGE_SIZE = 5000;

/**
 * Hard ceiling on total distinct products fetched during an export.
 * If the dataset exceeds this value a warning is logged and the loop stops
 * — prevents infinite pagination in unexpected edge cases.
 */
const EXPORT_MAX_PRODUCTS = 25000;

/**
 * Per-request timeout for export fetches (ms).
 * A single page of 5 000 products can be large — give the server more time
 * than the default 30 s client timeout.
 */
export const EXPORT_REQUEST_TIMEOUT_MS = 120_000;

/**
 * Filter params accepted by the full-dataset fetch utility.
 * Same shape as ProductsQueryParams but without pagination fields
 * (limit / offset are managed internally).
 */
export type ExportFilterParams = Omit<ProductsQueryParams, 'limit' | 'offset'>;

/**
 * Options for fetchAllFlatProducts.
 */
export interface FetchAllFlatOptions {
  /** AbortSignal — cancel the in-flight request immediately. */
  signal?: AbortSignal;
}

/**
 * Fetches ALL flat product rows matching the current filter by iterating
 * through pages of the /api/products/flat endpoint.
 *
 * Pagination follows Task 10 semantics: the API paginates by distinct
 * product_id (not by flat-row offset). Each page requests EXPORT_PAGE_SIZE
 * distinct products; the loop continues until either:
 *   - The response contains fewer products than requested (last page), or
 *   - The running total of distinct products reaches EXPORT_MAX_PRODUCTS
 *     (hard ceiling — logs a warning and stops), or
 *   - The provided AbortSignal is aborted (throws DOMException 'AbortError').
 *
 * @param service  An instantiated ProductService (caller owns auth/context).
 * @param params   Current filter / sort params (limit + offset omitted).
 * @param options  Optional abort signal and other fetch options.
 * @returns        Flat array of all FlatProduct rows across all pages.
 */
export async function fetchAllFlatProducts(
  service: ProductService,
  params: ExportFilterParams,
  options: FetchAllFlatOptions = {}
): Promise<FlatProduct[]> {
  const { signal } = options;
  const allRows: FlatProduct[] = [];
  let productOffset = 0;

  while (true) {
    if (signal?.aborted) {
      throw new DOMException('Export cancelled', 'AbortError');
    }

    const response = await service.getAllFlat(
      {
        ...params,
        limit: EXPORT_PAGE_SIZE,
        offset: productOffset,
      },
      { signal, timeout: EXPORT_REQUEST_TIMEOUT_MS }
    );

    const { products, totalProducts } = response.data;

    allRows.push(...products);

    // Distinct-product count returned by this page:
    // Because offset is product-based (Task 10), each page returns rows for
    // up to EXPORT_PAGE_SIZE distinct products. We derive the distinct-product
    // count for THIS page by checking how many unique product_ids we received.
    const distinctProductsThisPage = new Set(products.map(p => p.product_id))
      .size;

    productOffset += distinctProductsThisPage;

    // Stop when this page returned fewer distinct products than requested
    // (we have exhausted all pages).
    if (distinctProductsThisPage < EXPORT_PAGE_SIZE) {
      break;
    }

    // Hard ceiling guard — prevents runaway loops.
    if (productOffset >= EXPORT_MAX_PRODUCTS) {
      console.warn(
        `[fetchAllFlatProducts] Export ceiling reached: ${EXPORT_MAX_PRODUCTS} distinct products fetched. ` +
          `Total reported by API: ${totalProducts}. Some records may be omitted.`
      );
      break;
    }
  }

  return allRows;
}
